import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { parse } from "marked";
import * as speechsdk from 'microsoft-cognitiveservices-speech-sdk';

// Global speech manager to track currently playing speech
const globalSpeechManager = {
  activeSynthesizer: null,
  activeButtonInstance: null,
  stopCurrentSpeech: function() {
    if (this.activeSynthesizer && this.activeButtonInstance) {
      try {
        const audioConfig = this.activeSynthesizer.audioConfig;
        if (audioConfig && audioConfig.privDestination) {
          audioConfig.privDestination.pause();
        }
        this.activeSynthesizer.close();
        this.activeButtonInstance.handleStop();
      } catch (error) {
        console.error("Error stopping current speech:", error);
      }
      this.activeSynthesizer = null;
      this.activeButtonInstance = null;
    }
  },
  setActiveSpeech: function(synthesizer, buttonInstance) {
    this.stopCurrentSpeech();
    this.activeSynthesizer = synthesizer;
    this.activeButtonInstance = buttonInstance;
  }
};

const SpeechButton = forwardRef(({ 
  content, 
  className = "", 
  size = 32,
  showTimer = false,
  voiceName = "en-IN-NeerjaNeural",
  speechRate = 1,
  region = "eastus",
  onSpeechStart = () => {},
  onSpeechStop = () => {}
}, ref) => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [synthesizer, setSynthesizer] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0);
  const [synthesisResult, setSynthesisResult] = useState(null);
  
  const subscriptionKey = process.env.REACT_APP_AZURE_COGNITIVE_SPEECH_SUBSCRIPTION_KEY;

  const calculateSpeechDuration = (text, rate = 1) => {
    const wordsPerMinute = 150 * rate;
    const words = text.split(' ').length;
    return Math.ceil((words / wordsPerMinute) * 60 * 1000); // Duration in milliseconds
  };

  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const extractTextFromContent = (content) => {
    if (typeof content === 'string') {
      return content;
    }
    
    if (content && content.topic && content.highlights) {
      // For CurrentAffairs format
      return `${content.topic}. ${content.highlights
        .map(h => {
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = parse(h.highlight_text);
          return tempDiv.textContent;
        })
        .join(". ")}`;
    }
    
    if (content && typeof content === 'object') {
      // Try to extract text from object properties
      const textValues = Object.values(content)
        .filter(value => typeof value === 'string')
        .join('. ');
      return textValues;
    }
    
    return '';
  };

  const handleStop = () => {
    setIsSpeaking(false);
    setSynthesizer(null);
    setRemainingTime(0);
    setSynthesisResult(null);
    onSpeechStop();
  };

  const stopSpeech = () => {
    if (synthesizer) {
      try {
        const audioConfig = synthesizer.audioConfig;
        if (audioConfig && audioConfig.privDestination) {
          audioConfig.privDestination.pause();
        }
        synthesizer.close();
        handleStop();
        
        // Clear global state if this was the active speech
        if (globalSpeechManager.activeSynthesizer === synthesizer) {
          globalSpeechManager.activeSynthesizer = null;
          globalSpeechManager.activeButtonInstance = null;
        }
      } catch (error) {
        console.error("Error stopping speech:", error);
        handleStop();
      }
    }
  };

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    stopSpeech,
    isSpeaking,
    startSpeech: handleSpeech,
    handleStop
  }));

  const handleSpeech = async () => {
    try {
      // If already speaking, stop the speech
      if (isSpeaking) {
        stopSpeech();
        return;
      }

      if (!subscriptionKey) {
        console.error("Azure Speech subscription key not found");
        return;
      }

      const speechConfig = speechsdk.SpeechConfig.fromSubscription(
        subscriptionKey,
        region
      );
      
      speechConfig.speechSynthesisVoiceName = voiceName;
      speechConfig.speechSynthesisRate = speechRate;

      const audioConfig = speechsdk.AudioConfig.fromDefaultSpeakerOutput();
      const newSynthesizer = new speechsdk.SpeechSynthesizer(speechConfig, audioConfig);

      const textToSpeak = extractTextFromContent(content);
      
      if (!textToSpeak) {
        console.error("No text content found to speak");
        return;
      }

      // Stop any currently playing speech and set this as the active one
      globalSpeechManager.setActiveSpeech(newSynthesizer, {
        handleStop: () => {
          handleStop();
          setSynthesizer(null);
        }
      });

      // Calculate and set the duration
      const duration = calculateSpeechDuration(textToSpeak, speechRate);
      setRemainingTime(duration);
      setIsSpeaking(true);
      setSynthesizer(newSynthesizer);
      
      // Call the callback to notify parent
      onSpeechStart();

      // Create a promise that can be rejected to stop speech
      const speechPromise = new Promise((resolve, reject) => {
        newSynthesizer.speakTextAsync(
          textToSpeak,
          result => {
            if (result) {
              setSynthesisResult(result);
              if (result.errorDetails) {
                console.error("Speech synthesis error details:", result.errorDetails);
                reject(result.errorDetails);
              } else {
                resolve();
              }
            }
            // Only cleanup if we haven't already stopped
            if (synthesizer === newSynthesizer) {
              newSynthesizer.close();
              handleStop();
              // Clear global state if this was the active speech
              if (globalSpeechManager.activeSynthesizer === newSynthesizer) {
                globalSpeechManager.activeSynthesizer = null;
                globalSpeechManager.activeButtonInstance = null;
              }
            }
          },
          error => {
            console.error("Speech synthesis error:", error);
            newSynthesizer.close();
            handleStop();
            // Clear global state if this was the active speech
            if (globalSpeechManager.activeSynthesizer === newSynthesizer) {
              globalSpeechManager.activeSynthesizer = null;
              globalSpeechManager.activeButtonInstance = null;
            }
            reject(error);
          }
        );
      });

      return speechPromise;

    } catch (error) {
      console.error("Speech synthesis error:", error);
      handleStop();
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (synthesizer) {
        try {
          const audioConfig = synthesizer.audioConfig;
          if (audioConfig && audioConfig.privDestination) {
            audioConfig.privDestination.pause();
          }
          synthesizer.close();
          handleStop();
          // Clear global state if this was the active speech
          if (globalSpeechManager.activeSynthesizer === synthesizer) {
            globalSpeechManager.activeSynthesizer = null;
            globalSpeechManager.activeButtonInstance = null;
          }
        } catch (error) {
          console.error("Error cleaning up synthesizer:", error);
        }
      }
    };
  }, [synthesizer]);

  return (
    <div 
      onClick={handleSpeech}
      style={{ cursor: "pointer" }}
      className={className}
    >
      {isSpeaking ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Volume2
            className="speaker-icon speaking"
            size={size}
            color="#706BAA"
            style={{ color: "#706BAA" }}
            onClick={(e) => {
              e.stopPropagation();
              stopSpeech();
            }}
          />
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <VolumeX
            className="speaker-icon"
            size={size}
            color="#706BAA"
            onClick={(e) => {
              e.stopPropagation();
              handleSpeech();
            }}
          />
        </div>
      )}
    </div>
  );
});

SpeechButton.displayName = 'SpeechButton';

export default SpeechButton; 